import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BookOpen, ArrowLeft, Send, CheckCircle, User, Mail, Building, Briefcase
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoRequestPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    etablissement: "",
    fonction: "",
    message: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.prenom || !formData.nom || !formData.email || !formData.etablissement || !formData.fonction) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/demo/request`, formData);
      setSubmitted(true);
      toast.success("Demande envoyée avec succès !");
    } catch (error) {
      console.error("Error submitting demo request:", error);
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen hero-section flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#22C55E] flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-medium text-[#1A365D] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Demande envoyée !
            </h2>
            <p className="text-gray-600 mb-6">
              Merci pour votre intérêt pour SophIA. Nous vous contacterons sous 24h avec vos accès de démonstration.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="bg-[#1A365D] hover:bg-[#132745]"
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-section flex items-center justify-center p-4" data-testid="demo-request-page">
      <Card className="max-w-lg w-full border-0 shadow-2xl bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-lg bg-[#1A365D] flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Demander un accès démo
          </CardTitle>
          <CardDescription>
            Découvrez SophIA gratuitement avec un accès complet à notre plateforme de démonstration
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prenom" className="text-[#0F172A]">
                  <User className="w-4 h-4 inline mr-1" />
                  Prénom *
                </Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => handleChange("prenom", e.target.value)}
                  placeholder="Votre prénom"
                  className="mt-1"
                  required
                  data-testid="demo-prenom-input"
                />
              </div>
              <div>
                <Label htmlFor="nom" className="text-[#0F172A]">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => handleChange("nom", e.target.value)}
                  placeholder="Votre nom"
                  className="mt-1"
                  required
                  data-testid="demo-nom-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0F172A]">
                <Mail className="w-4 h-4 inline mr-1" />
                Email professionnel *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="votre.email@etablissement.fr"
                className="mt-1"
                required
                data-testid="demo-email-input"
              />
            </div>

            <div>
              <Label htmlFor="etablissement" className="text-[#0F172A]">
                <Building className="w-4 h-4 inline mr-1" />
                Établissement *
              </Label>
              <Input
                id="etablissement"
                value={formData.etablissement}
                onChange={(e) => handleChange("etablissement", e.target.value)}
                placeholder="Nom de votre établissement"
                className="mt-1"
                required
                data-testid="demo-etablissement-input"
              />
            </div>

            <div>
              <Label htmlFor="fonction" className="text-[#0F172A]">
                <Briefcase className="w-4 h-4 inline mr-1" />
                Fonction *
              </Label>
              <Select value={formData.fonction} onValueChange={(v) => handleChange("fonction", v)}>
                <SelectTrigger className="mt-1" data-testid="demo-fonction-select">
                  <SelectValue placeholder="Sélectionnez votre fonction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="directeur">Directeur / Directrice</SelectItem>
                  <SelectItem value="directeur_adjoint">Directeur adjoint</SelectItem>
                  <SelectItem value="professeur_philo">Professeur de Philosophie</SelectItem>
                  <SelectItem value="professeur_autre">Autre Professeur</SelectItem>
                  <SelectItem value="responsable_pedagogique">Responsable pédagogique</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="message" className="text-[#0F172A]">
                Message (optionnel)
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                placeholder="Parlez-nous de vos besoins..."
                className="mt-1 min-h-20"
                data-testid="demo-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A] font-medium"
              data-testid="demo-submit-btn"
            >
              {loading ? (
                "Envoi en cours..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Demander mon accès démo
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="w-full text-gray-500"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </Button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4 mb-2">
            En soumettant ce formulaire, vous acceptez d'être contacté par SophIA.
          </p>
          <Footer minimal />
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoRequestPage;
